import type { PromptRun } from "@/lib/api/types";
import { formatCount, formatDateTime, formatEuro } from "@/lib/format";
import { RUN_KIND_LABEL, RUN_STATUS_LABEL, runSettings } from "@/lib/prompt-module";

type Props = {
  /** `null` solange die Läufe laden. */
  runs: PromptRun[] | null;
  error: string | null;
  openId: number | null;
  onOpen: (run: PromptRun) => void;
  onClose: () => void;
};

/**
 * Verlauf: Jeder Lauf (Generieren, Validieren, Trigger-Test) bleibt gespeichert, auch wenn er noch läuft,
 * wartet oder fehlgeschlagen ist. „Öffnen“ zeigt die Teilschritte und Ergebnisse wie beim Start.
 */
export function RunsPanel({ runs, error, openId, onOpen, onClose }: Props) {
  return (
    <section aria-labelledby="runs-title" className="flex flex-col gap-3 rounded-lg border border-line bg-white px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h2 id="runs-title" className="text-base font-semibold">
            Verlauf der Läufe
          </h2>
          <span className="text-xs text-muted">
            Alle Läufe dieses Projekts, neueste zuerst, in jedem Zustand. Vorgaben, Schritte und Ergebnisse bleiben erhalten.
          </span>
        </div>
        <button type="button" onClick={onClose} className="h-11 rounded-md border border-field bg-white px-3.5 text-sm">
          Schließen
        </button>
      </div>

      {error && (
        <p role="alert" className="text-[13px] text-danger">
          Der Verlauf konnte nicht geladen werden: {error}
        </p>
      )}
      {runs === null && !error && <p className="text-sm text-muted">Verlauf wird geladen …</p>}
      {runs !== null && runs.length === 0 && <p className="text-sm text-muted">Noch keine Läufe. Starte eine Generierung oder einen Trigger-Test.</p>}

      {runs !== null && runs.length > 0 && (
        <div className="overflow-x-auto">
          <table aria-label="Läufe" className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="px-3 py-2 font-medium">Gestartet</th>
                <th className="px-3 py-2 font-medium">Art</th>
                <th className="px-3 py-2 font-medium">Topic</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Vorgaben und Ergebnis</th>
                <th className="px-3 py-2 text-right font-medium">Kosten</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.job_id} className={`border-t border-neutral-bg align-top ${run.job_id === openId ? "bg-[#eef3fa]" : ""}`}>
                  <td className="whitespace-nowrap px-3 py-2.5 text-[13px]">{formatDateTime(run.created_at)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-[13px]">{RUN_KIND_LABEL[run.kind]}</td>
                  <td className="px-3 py-2.5 text-[13px]">{run.topic_name ?? "–"}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-[13px]">
                    {RUN_STATUS_LABEL[run.status] ?? run.status}
                    {run.status === "running" && ` (${formatCount(run.progress)} %)`}
                  </td>
                  <td className="px-3 py-2.5 text-[13px]">
                    <div>{runSettings(run)}</div>
                    {run.summary && <div className="text-xs text-muted">{run.summary}</div>}
                    {run.error && <div className="text-xs text-danger">{run.error}</div>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono text-[13px]">
                    {run.cost_eur > 0 ? formatEuro(run.cost_eur) : run.estimated_cost_eur > 0 ? `Schätzung ${formatEuro(run.estimated_cost_eur)}` : "–"}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => onOpen(run)}
                      aria-label={`${RUN_KIND_LABEL[run.kind]} vom ${formatDateTime(run.created_at)} öffnen`}
                      className="h-11 rounded-md border border-field bg-white px-3 text-[13px]"
                    >
                      Öffnen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
